import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Plan } from 'src/pages/deprecated/payment/types';
import { TagData } from './signup-workflow/types';
import {
  ChallengePlan,
  ShopifyVariant,
  ChallengeSelectionPreviewItem,
} from 'src/pages/types';
import { ShippingAddressFormValues } from 'src/pages/payment-new/components/address-fields/types';
import { getCoachingAddonFromSession } from 'src/util/addon-storage.util';

export type User = { firstName?: string; lastName?: string; email: string };
export interface InitialStateType {
  currentStep: number;
  selectedPlanId: string;
  allPlansSorted: Plan[];
  availablePlans: Plan[];
  coupon: string;
  couponObj: any;
  addonWeek: number;
  registeredUser: User | {};
  isChallengeAccepted: boolean;
  futureCommencement: boolean;
  challengeData: TagData | null;
  isFitherProjectUser: boolean;
  noIntentAvailable: boolean;
  showFreeTrial: boolean;
  challengePackageSelectedPlan: ChallengePlan | null;
  isChallengePackageFlow: boolean;
  isChallengeBundleFlow: boolean;
  isPersonalCoachingAdded?: boolean;
  isNutritionCoachingAdded?: boolean;
  isSupplementAdded?: boolean;
  isSupplementAddedByGift?: boolean;
  shippingAddress?: ShippingAddressFormValues;
  proteinFlavor: string | null;
  challengeStartDate?: string;
  challengeEndDate?: string;
  shopifyVariants: ShopifyVariant[];
  shopifyVariantsLoaded: boolean;
  isBypassPlanSelection: boolean;
  challengeSelectionPreview: ChallengeSelectionPreviewItem[] | null;
}

const initialState: InitialStateType = {
  currentStep: 1,
  selectedPlanId: '',
  allPlansSorted: [],
  availablePlans: [],
  coupon: '',
  couponObj: {},
  addonWeek: 1,
  registeredUser: {},
  isChallengeAccepted: false,
  futureCommencement: false,
  challengeData: null,
  isFitherProjectUser: false,
  noIntentAvailable: false,
  showFreeTrial: false,
  challengePackageSelectedPlan: null,
  isChallengePackageFlow: false,
  isChallengeBundleFlow: false,
  isPersonalCoachingAdded: false,
  isNutritionCoachingAdded: getCoachingAddonFromSession(),
  isSupplementAdded: false,
  isSupplementAddedByGift: false,
  proteinFlavor: null,
  shopifyVariants: [],
  shopifyVariantsLoaded: false,
  isBypassPlanSelection: false,
  challengeSelectionPreview: null,
};

export const signUpSlice = createSlice({
  name: 'signUp',
  initialState,
  reducers: {
    setSignupStep: (state, action: PayloadAction<number>) => {
      state.currentStep = action.payload;
    },
    setIsChallengeAccepted: (state, action: PayloadAction<boolean>) => {
      state.isChallengeAccepted = action.payload;
    },
    setIsFutureCommencement: (state, action: PayloadAction<boolean>) => {
      state.futureCommencement = action.payload;
    },
    setChallengeData: (state, action: PayloadAction<TagData | null>) => {
      state.challengeData = action.payload;
    },
    setIsFitherProjectUser: (state, action: PayloadAction<boolean>) => {
      state.isFitherProjectUser = action.payload;
    },
    setNoPaymentIntentAvailable: (state, action: PayloadAction<boolean>) => {
      state.noIntentAvailable = action.payload;
    },
    setSelectedPlanId: (state, action: PayloadAction<string>) => {
      state.selectedPlanId = action.payload;
      state.couponObj = {};
    },
    setAvailablePlans: (state, action: PayloadAction<Plan[]>) => {
      state.availablePlans = action.payload;
      if (action.payload && action.payload[0]) {
        state.selectedPlanId = action.payload[0].id; //default to first plan because of design, we need to select a plan
      }
      state.couponObj = {};
    },
    setAvailablePlansWithoutDefaultCheck: (
      state,
      action: PayloadAction<Plan[]>
    ) => {
      state.availablePlans = action.payload;
      state.couponObj = {};
    },
    setAddonWeek: (state, action: PayloadAction<number>) => {
      state.addonWeek = action.payload;
    },
    setCouponObj: (state, action: PayloadAction<any>) => {
      state.couponObj = action.payload;
    },
    setAllPlansSorted: (state, action: PayloadAction<Plan[]>) => {
      state.allPlansSorted = action.payload;
    },
    handleProspectUserRedirect: state => {
      state.availablePlans = [];
      state.couponObj = {};
    },
    setSignUpUser: (
      state,
      action: PayloadAction<{
        firstName: string;
        lastName: string;
        email: string;
      }>
    ) => {
      state.registeredUser = action.payload;
    },
    setFreeTrial: (state, action: PayloadAction<boolean>) => {
      state.showFreeTrial = action.payload;
    },
    setIsChallengePackageFlow: (state, action: PayloadAction<boolean>) => {
      state.isChallengePackageFlow = action.payload;
    },
    setIsChallengeBundleFlow: (state, action: PayloadAction<boolean>) => {
      state.isChallengeBundleFlow = action.payload;
    },
    setIsPersonalCoachingAdded: (state, action: PayloadAction<boolean>) => {
      state.isPersonalCoachingAdded = action.payload;
    },
    setIsNutritionCoachingAdded: (state, action: PayloadAction<boolean>) => {
      state.isNutritionCoachingAdded = action.payload;
    },
    setIsSupplementAdded: (state, action: PayloadAction<boolean>) => {
      state.isSupplementAdded = action.payload;
      state.isSupplementAddedByGift = false;
    },
    setSupplementAddedByGift: state => {
      state.isSupplementAdded = true;
      state.isSupplementAddedByGift = true;
    },
    setChallengePlan: (state, action: PayloadAction<ChallengePlan | null>) => {
      if (!action.payload) {
        state.isChallengePackageFlow = false;
        state.isChallengeBundleFlow = false;
        state.isPersonalCoachingAdded = false;
        state.challengePackageSelectedPlan = null;
        state.proteinFlavor = null;
        return;
      }
      state.isChallengePackageFlow = true;
      state.challengePackageSelectedPlan = action.payload;
    },
    setShippingAddressValues: (
      state,
      action: PayloadAction<ShippingAddressFormValues>
    ) => {
      state.shippingAddress = action.payload;
    },
    resetShippingAddress: state => {
      state.shippingAddress = undefined;
    },
    setProteinFlavorValue: (state, action: PayloadAction<string>) => {
      state.proteinFlavor = action.payload;
    },
    resetProteinFlavor: state => {
      state.proteinFlavor = null;
    },
    setChallengeStartDate: (state, action: PayloadAction<string>) => {
      state.challengeStartDate = action.payload;
    },
    setChallengeEndDate: (state, action: PayloadAction<string>) => {
      state.challengeEndDate = action.payload;
    },
    setShopifyVariants: (state, action: PayloadAction<ShopifyVariant[]>) => {
      state.shopifyVariants = action.payload;
      state.shopifyVariantsLoaded = true;
    },
    setIsBypassPlanSelection: (state, action: PayloadAction<boolean>) => {
      state.isBypassPlanSelection = action.payload;
    },
    setChallengeSelectionPreview: (
      state,
      action: PayloadAction<ChallengeSelectionPreviewItem[] | null>
    ) => {
      state.challengeSelectionPreview = action.payload;
    },
  },
});

export const {
  setSignupStep,
  setIsChallengeAccepted,
  setIsFutureCommencement,
  setChallengeData,
  setIsFitherProjectUser,
  setNoPaymentIntentAvailable,
  setSelectedPlanId,
  setAllPlansSorted,
  setAvailablePlans,
  setAvailablePlansWithoutDefaultCheck,
  setAddonWeek,
  setSignUpUser,
  setCouponObj,
  handleProspectUserRedirect,
  setFreeTrial,
  setChallengePlan,
  setIsChallengePackageFlow,
  setIsChallengeBundleFlow,
  setIsPersonalCoachingAdded,
  setIsNutritionCoachingAdded,
  setIsSupplementAdded,
  setSupplementAddedByGift,
  setShippingAddressValues,
  setProteinFlavorValue,
  resetProteinFlavor,
  resetShippingAddress,
  setChallengeStartDate,
  setChallengeEndDate,
  setShopifyVariants,
  setIsBypassPlanSelection,
  setChallengeSelectionPreview,
} = signUpSlice.actions;

export default signUpSlice.reducer;
